import { Injectable, Signal, signal } from '@angular/core';
import { SkyCatalog, SkyNamedStar, SkyStar } from './hd-fixed-star-sky.util';

interface StarsGeoJson {
  readonly features: readonly {
    readonly properties: { readonly mag: number; readonly bv?: string };
    readonly geometry: { readonly coordinates: readonly [number, number] };
  }[];
}

interface LinesGeoJson {
  readonly features: readonly {
    readonly id: string;
    readonly geometry: { readonly coordinates: readonly (readonly (readonly number[])[])[] };
  }[];
}

const STARS_URL = 'assets/human-design/fixed-stars/stars.6.json';
const LINES_URL = 'assets/human-design/fixed-stars/constellations.lines.json';
const NAMED_URL = 'assets/human-design/fixed-stars/named-stars.json';

/**
 * Laadt de gebundelde sterrencatalogus (Hipparcos, magnitude ≤ 6) en de
 * sterrenbeeld-lijnen éénmalig als statische asset en cachet ze. Bewust met
 * `fetch` i.p.v. `HttpClient`: het zijn publieke assets die niet langs de
 * auth-interceptors hoeven. De catalogus is een signal zodat de kaart-componenten
 * (OnPush) automatisch hertekenen zodra de data binnen is.
 */
@Injectable({ providedIn: 'root' })
export class HdFixedStarSkyCatalogService {
  private readonly catalogSignal = signal<SkyCatalog | null>(null);
  private started = false;

  /** De geladen catalogus, of `null` zolang die nog binnenkomt. */
  get catalog(): Signal<SkyCatalog | null> {
    return this.catalogSignal.asReadonly();
  }

  /** Start het laden (idempotent). Roep aan bij het eerste kaartgebruik. */
  ensureLoaded(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    void this.load();
  }

  private async load(): Promise<void> {
    try {
      const [starsRes, linesRes, namedRes] = await Promise.all([fetch(STARS_URL), fetch(LINES_URL), fetch(NAMED_URL)]);
      if (!starsRes.ok || !linesRes.ok || !namedRes.ok) {
        throw new Error(`sterrencatalogus HTTP ${starsRes.status}/${linesRes.status}/${namedRes.status}`);
      }
      const starsJson = (await starsRes.json()) as StarsGeoJson;
      const linesJson = (await linesRes.json()) as LinesGeoJson;
      const namedJson = (await namedRes.json()) as readonly SkyNamedStar[];
      this.catalogSignal.set(this.parse(starsJson, linesJson, namedJson));
    } catch (error) {
      console.error('Kon de fixed-star-sterrencatalogus niet laden.', error);
    }
  }

  private parse(starsJson: StarsGeoJson, linesJson: LinesGeoJson, named: readonly SkyNamedStar[]): SkyCatalog {
    const stars: SkyStar[] = starsJson.features.map(feature => {
      const [ra, dec] = feature.geometry.coordinates;
      return {
        ra: ra < 0 ? ra + 360 : ra,
        dec,
        mag: feature.properties.mag,
        bv: feature.properties.bv != null ? parseFloat(feature.properties.bv) : NaN,
      };
    });
    const lines = new Map<string, readonly (readonly (readonly number[])[])[]>();
    for (const feature of linesJson.features) {
      lines.set(feature.id, feature.geometry.coordinates);
    }
    return { stars, lines, named };
  }
}
